(() => {
  const registerProductFaqDialog = () => {
    if (customElements.get('product-faq-dialog')) return;

    class ProductFaqDialog extends HTMLElement {
      connectedCallback() {
        if (this.dataset.faqDialogInitialized === 'true') return;
        this.dataset.faqDialogInitialized = 'true';

        this.closeButton = this.querySelector('[data-product-faq-close], [id^="ModalClose-"]');
        this.overlay = this.querySelector('[data-product-faq-overlay]');
        this.dialog = this.querySelector('[role="dialog"]');
        this.panel = this.querySelector('.product-faq-modal__content');
        this.focusTransitionTimer = null;
        this.focusTransitionHandler = null;

        this.onCloseClick = () => this.hide();
        this.onSurfaceClick = (event) => {
          if (!this.classList.contains('is-open')) return;
          if (this.panel && !this.panel.contains(event.target)) this.hide();
        };
        this.onKeyup = (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
        };

        this.closeButton?.addEventListener('click', this.onCloseClick);
        this.addEventListener('click', this.onSurfaceClick);
        this.addEventListener('keyup', this.onKeyup);

        /*
         * FAQ is a fixed surface, like Collection Studio. Move it to body once
         * so transformed/overflowing product ancestors can never become geometry
         * owners. Unlike Shopify ModalDialog, this component does not inherit any
         * generic modal open/close state.
         */
        if (this.parentElement !== document.body) {
          this.dataset.section = this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || this.dataset.section || '';
          document.body.appendChild(this);
        }

        this.setAttribute('aria-hidden', 'true');
        this.surfaceName = this.surfaceName || `faq:${this.id || this.dataset.section || 'dialog'}`;
        if (window.BrilintonSurfaceManager) {
          window.BrilintonSurfaceManager.register(this.surfaceName, {
            root: () => this,
            close: () => this.hide(),
          });
        }
      }

      clearFocusTransition() {
        window.clearTimeout(this.focusTransitionTimer);
        this.focusTransitionTimer = null;
        if (this.panel && this.focusTransitionHandler) {
          this.panel.removeEventListener('transitionend', this.focusTransitionHandler);
        }
        this.focusTransitionHandler = null;
      }

      trapFocusAfterEntry() {
        this.clearFocusTransition();
        const finish = (event) => {
          if (event && (event.target !== this.panel || event.propertyName !== 'transform')) return;
          this.clearFocusTransition();
          if (!this.classList.contains('is-open')) return;
          trapFocus(this, this.dialog || this);
        };

        this.focusTransitionHandler = finish;
        this.panel?.addEventListener('transitionend', finish);
        this.focusTransitionTimer = window.setTimeout(() => finish(), 460);
      }

      show(opener) {
        if (this.classList.contains('is-open')) return;

        this.openedBy = opener || null;
        const popup = this.querySelector('.template-popup');

        if (window.BrilintonSurfaceManager) {
          window.BrilintonSurfaceManager.register(this.surfaceName, {
            root: () => this,
            close: () => this.hide(),
          });
          window.BrilintonSurfaceManager.open(this.surfaceName, {
            trigger: opener,
            lockMode: 'overflow',
          });
        } else {
          document.body.classList.add('overflow-hidden');
        }

        if (popup) popup.loadContent();

        /* Exact Collection Studio contract: one class owns visibility, mask and
           panel transform. No open attribute, ModalDialog state or RAF staging. */
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        this.trapFocusAfterEntry();
        window.pauseAllMedia();
      }

      hide() {
        if (!this.classList.contains('is-open')) return;

        this.clearFocusTransition();
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');

        if (window.BrilintonSurfaceManager) {
          removeTrapFocus();
          window.BrilintonSurfaceManager.close(this.surfaceName, { restoreFocus: true });
        } else {
          document.body.classList.remove('overflow-hidden');
          removeTrapFocus(this.openedBy);
        }

        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        window.pauseAllMedia();
      }
    }

    customElements.define('product-faq-dialog', ProductFaqDialog);
  };

  registerProductFaqDialog();
  if (!customElements.get('product-faq-rotator')) {
    class ProductFaqRotator extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.slides = Array.from(this.querySelectorAll('[data-product-faq-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-product-faq-dot]'));
        this.dotWindow = this.querySelector('.product-faq__controls');
        this.dotRail = this.querySelector('.product-faq__dots--vertical');
        this.openerButton = this.querySelector('.product-faq__trigger');
        this.modalSelector = this.dataset.productFaqModal;
        this.interval = Number.parseInt(this.dataset.productFaqInterval || '4000', 10);
        this.activeIndex = 0;
        this.timer = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.slides.length || !this.openerButton) return;

        this.render(0);

        this.onPointerEnter = () => this.stop();
        this.onPointerLeave = () => this.start();
        this.onFocusIn = () => this.stop();
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.start();
        };
        this.onVisibilityChange = () => {
          if (document.hidden) this.stop();
          else this.start();
        };
        this.onClick = () => this.stop();
        this.onModalClosed = () => this.start();
        this.onMotionChange = () => {
          this.stop();
          this.start();
        };

        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.openerButton.addEventListener('click', this.onClick);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.body.addEventListener('modalClosed', this.onModalClosed);

        if (this.reduceMotion.addEventListener) {
          this.reduceMotion.addEventListener('change', this.onMotionChange);
        } else if (this.reduceMotion.addListener) {
          this.reduceMotion.addListener(this.onMotionChange);
        }

        this.start();
        this.openAfterQuestionResponse();
      }

      disconnectedCallback() {
        this.stop();
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.openerButton?.removeEventListener('click', this.onClick);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.body.removeEventListener('modalClosed', this.onModalClosed);

        if (this.reduceMotion?.removeEventListener) {
          this.reduceMotion.removeEventListener('change', this.onMotionChange);
        } else if (this.reduceMotion?.removeListener) {
          this.reduceMotion.removeListener(this.onMotionChange);
        }
      }

      start() {
        if (
          this.timer ||
          this.slides.length < 2 ||
          document.hidden ||
          this.reduceMotion.matches ||
          document.body.classList.contains('overflow-hidden')
        ) return;

        this.timer = window.setInterval(() => {
          this.render((this.activeIndex + 1) % this.slides.length);
        }, this.interval);
      }

      stop() {
        if (!this.timer) return;
        window.clearInterval(this.timer);
        this.timer = null;
      }

      render(index) {
        this.activeIndex = index;
        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        this.dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
        });
        this.positionDotRail(index);

        const activeQuestion = this.slides[index]?.textContent?.trim();
        if (activeQuestion) {
          this.openerButton.setAttribute('aria-label', `Open FAQ and policies: ${activeQuestion}`);
        }
      }

      positionDotRail(index) {
        if (!this.dotWindow || !this.dotRail || this.dots.length < 2) return;

        window.requestAnimationFrame(() => {
          const activeDot = this.dots[index];
          if (!activeDot || !this.dotWindow?.isConnected || !this.dotRail?.isConnected) return;

          const windowHeight = this.dotWindow.clientHeight;
          const railHeight = this.dotRail.scrollHeight;
          const maxShift = Math.max(0, railHeight - windowHeight);
          const desiredShift = activeDot.offsetTop + (activeDot.offsetHeight / 2) - (windowHeight / 2);
          const shift = Math.max(0, Math.min(desiredShift, maxShift));

          this.dotRail.style.transform = `translate3d(0, -${Math.round(shift)}px, 0)`;
        });
      }

      openAfterQuestionResponse() {
        if (!this.modalSelector) return;

        customElements.whenDefined('product-faq-dialog').then(() => {
          const modal = document.querySelector(this.modalSelector);
          if (!modal || !modal.querySelector('[data-product-faq-reopen]')) return;

          window.requestAnimationFrame(() => {
            if (typeof modal.show === 'function') modal.show(this.openerButton);
          });
        });
      }
    }

    customElements.define('product-faq-rotator', ProductFaqRotator);
  }

  if (!customElements.get('product-faq-tabs')) {
    class ProductFaqTabs extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.tabs = Array.from(this.querySelectorAll('[data-product-faq-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-product-faq-panel]'));
        if (!this.tabs.length || !this.panels.length) return;

        this.onClick = (event) => {
          const tab = event.target.closest('[data-product-faq-tab]');
          if (tab && this.contains(tab)) this.activate(tab, true);
        };
        this.onKeydown = (event) => {
          const currentIndex = this.tabs.indexOf(event.target);
          if (currentIndex < 0) return;

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = this.tabs.length - 1;
          else return;

          event.preventDefault();
          this.activate(this.tabs[nextIndex], true);
        };

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
      }

      activate(tab, focus = false) {
        const panelId = tab.getAttribute('aria-controls');
        this.tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        this.panels.forEach((panel) => {
          const active = panel.id === panelId;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (focus) tab.focus();
        const scroller = this.querySelector('.product-faq-tabs__panels');
        if (scroller) scroller.scrollTop = 0;
      }
    }

    customElements.define('product-faq-tabs', ProductFaqTabs);
  }

  if (!customElements.get('product-faq-ask')) {
    class ProductFaqAsk extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.form = this.querySelector('form');
        this.questionStep = this.querySelector('[data-product-faq-question-step]');
        this.contactStep = this.querySelector('[data-product-faq-contact-step]');
        this.question = this.querySelector('[data-product-faq-question]');
        this.questionError = this.querySelector('[data-product-faq-question-error]');
        this.continueButton = this.querySelector('[data-product-faq-continue]');

        if (!this.form || !this.question || !this.questionStep || !this.contactStep) return;

        this.onContinue = () => this.showContactStep();
        this.onQuestionInput = () => {
          if (this.question.value.trim()) this.setQuestionError(false);
        };
        this.onQuestionKeydown = (event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            this.showContactStep();
          }
        };
        this.onSubmit = (event) => {
          if (!this.question.value.trim()) {
            event.preventDefault();
            this.showQuestionStep();
            this.setQuestionError(true);
            return;
          }
          this.captureSelectionContext();
        };

        this.continueButton?.addEventListener('click', this.onContinue);
        this.question.addEventListener('input', this.onQuestionInput);
        this.question.addEventListener('keydown', this.onQuestionKeydown);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.continueButton?.removeEventListener('click', this.onContinue);
        this.question?.removeEventListener('input', this.onQuestionInput);
        this.question?.removeEventListener('keydown', this.onQuestionKeydown);
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      showContactStep() {
        const value = this.question.value.trim();
        if (!value) {
          this.setQuestionError(true);
          this.question.focus();
          return;
        }

        this.setQuestionError(false);
        this.questionStep.hidden = true;
        this.contactStep.hidden = false;
        const firstField = this.contactStep.querySelector('input:not([type="hidden"])');
        window.requestAnimationFrame(() => firstField?.focus());
      }

      showQuestionStep() {
        this.contactStep.hidden = true;
        this.questionStep.hidden = false;
        window.requestAnimationFrame(() => this.question.focus());
      }

      setQuestionError(show) {
        if (!this.questionError) return;
        this.questionError.hidden = !show;
        this.question.setAttribute('aria-invalid', show ? 'true' : 'false');
      }

      captureSelectionContext() {
        const selectors = {
          model: '[data-product-option-group="model"] .selected-value, [data-royal-pop-selected-label]',
          color: '[data-product-option-group="color"] .selected-value',
          bandware: '[data-b1-frame-bandware-option] .selected-value, [data-product-option-group="bandware"] .selected-value',
        };

        Object.entries(selectors).forEach(([key, selector]) => {
          const input = this.form.querySelector(`[data-product-faq-context="${key}"]`);
          if (!input) return;
          input.value = document.querySelector(selector)?.textContent?.trim() || '';
        });
      }
    }

    customElements.define('product-faq-ask', ProductFaqAsk);
  }
})();
